import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, User, Building, Home, UserPlus, Wallet, ChevronRight, ChevronLeft, AlertCircle } from 'lucide-react';
import api from '@/utils/api';
import { showSuccess, showError } from '@/utils/toast';

const LeadForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [activeSection, setActiveSection] = useState(0);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    customer_name: '', mother_name: '', date_of_birth: '', marital_status: '',
    pan_number: '', aadhaar_number: '', mobile_number: '', email: '',
    firm_name: '', firm_registration_date: '', firm_address: '', firm_landmark: '',
    current_address: '', current_address_landmark: '',
    permanent_address: '', permanent_address_landmark: '',
    alternate_number: '', alternate_email: '',
    city: '', state: '', pin_code: '',
    reference1: { name: '', mobile: '', relation: '' },
    reference2: { name: '', mobile: '', relation: '' },
    has_co_applicant: false,
    co_applicant_name: '', co_applicant_mother_name: '', co_applicant_mobile: '', co_applicant_email: '', co_applicant_relation: '',
    loan_type: '', loan_amount: '', monthly_income: '',
    business_vintage: '', existing_emi: '', cibil_score: '',
    lead_source: '', assigned_to: '', status: 'fresh',
    // Personal Loan specific fields
    qualification: '', office_address: '', office_landmark: '',
    date_of_joining: '', total_work_experience: '', current_company_work_experience: '',
    current_company_name: '', designation: '',
  });

  useEffect(() => {
    fetchUsers();
    fetchProducts();
    fetchLeadSources();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.filter((u) => ['telecaller', 'sales_executive', 'team_leader', 'branch_manager', 'asm', 'area_manager', 'regional_manager', 'zonal_manager', 'operations_executive'].includes(u.role)));
    } catch (e) { console.warn(e?.message); }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products', { params: { active_only: true } });
      setProducts(response.data || []);
    } catch (e) { console.warn(e?.message); }
  };

  const fetchLeadSources = async () => {
    try {
      const response = await api.get('/lead-sources', { params: { active_only: true } });
      setLeadSources(response.data || []);
    } catch (e) { console.warn(e?.message); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => { const copy = { ...prev }; delete copy[name]; return copy; });
    }
  };

  const handleRefChange = (refKey, field, value) => {
    setFormData((prev) => ({ ...prev, [refKey]: { ...prev[refKey], [field]: value } }));
  };

  // Mandatory fields with their section index
  const mandatoryFields = [
    { name: 'customer_name', label: 'Applicant Name', section: 0 },
    { name: 'mobile_number', label: 'Mobile Number', section: 0 },
    { name: 'city', label: 'City', section: 2 },
    { name: 'state', label: 'State', section: 2 },
    { name: 'loan_type', label: 'Product Type (Loan Type)', section: 4 },
    { name: 'loan_amount', label: 'Loan Amount', section: 4 },
    { name: 'lead_source', label: 'Lead Source', section: 4 },
    { name: 'assigned_to', label: 'Assigned Executive', section: 4 },
  ];

  const validateForm = () => {
    const newErrors = {};
    let firstInvalidSection = null;

    mandatoryFields.forEach((field) => {
      const value = formData[field.name];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        newErrors[field.name] = `${field.label} is required`;
        if (firstInvalidSection === null) firstInvalidSection = field.section;
      }
    });

    // Mobile number format
    if (formData.mobile_number && !/^\d{10}$/.test(formData.mobile_number.replace(/\s+/g, ''))) {
      newErrors.mobile_number = 'Enter a valid 10-digit mobile number';
      if (firstInvalidSection === null) firstInvalidSection = 0;
    }

    // PAN format (if provided)
    if (formData.pan_number && !/^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(formData.pan_number)) {
      newErrors.pan_number = 'Enter a valid PAN (e.g., ABCDE1234F)';
      if (firstInvalidSection === null) firstInvalidSection = 0;
    }

    // Loan amount > 0
    if (formData.loan_amount && parseFloat(formData.loan_amount) <= 0) {
      newErrors.loan_amount = 'Loan amount must be greater than zero';
      if (firstInvalidSection === null) firstInvalidSection = 4;
    }

    setErrors(newErrors);
    if (firstInvalidSection !== null) {
      setActiveSection(firstInvalidSection);
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);
    try {
      const submitData = { ...formData };
      ['loan_amount', 'monthly_income', 'existing_emi'].forEach((k) => {
        if (submitData[k]) submitData[k] = parseFloat(submitData[k]);
        else delete submitData[k];
      });
      if (submitData.cibil_score) submitData.cibil_score = parseInt(submitData.cibil_score);
      // Strip empty references
      if (!submitData.reference1.name && !submitData.reference1.mobile) delete submitData.reference1;
      if (!submitData.reference2.name && !submitData.reference2.mobile) delete submitData.reference2;

      const response = await api.post('/leads', submitData);
      showSuccess(`Lead Created Successfully — ${response.data.lead_number}`);
      navigate('/leads');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        showError(detail.map((d) => d.msg).join(', '));
      } else {
        showError(detail || 'Failed to create lead. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const sections = [
    { id: 0, title: 'Applicant Details', icon: User },
    { id: 1, title: 'Business/Firm', icon: Building },
    { id: 2, title: 'Residential', icon: Home },
    { id: 3, title: 'References', icon: UserPlus },
    { id: 4, title: 'Loan Details', icon: Wallet },
  ];

  const inputCls = (fieldName) =>
    `w-full px-4 py-3 rounded-lg border bg-background text-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all ${
      errors[fieldName] ? 'border-destructive' : 'border-border'
    }`;

  const ErrorMsg = ({ field }) =>
    errors[field] ? (
      <p className="text-xs text-destructive mt-1 flex items-center gap-1">
        <AlertCircle size={12} />
        {errors[field]}
      </p>
    ) : null;

  const sectionHasError = (sectionId) =>
    mandatoryFields.some((f) => f.section === sectionId && errors[f.name]) ||
    Object.keys(errors).some((k) => {
      const f = mandatoryFields.find((mf) => mf.name === k);
      return f && f.section === sectionId;
    });

  return (
    <DashboardLayout>
      <div>
        <button onClick={() => navigate('/leads')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors">
          <ArrowLeft size={20} />
          Back to Leads
        </button>

        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Create New Lead</h1>
          <p className="text-muted-foreground">Fill in customer details across all sections. <span className="text-destructive">*</span> indicates mandatory fields.</p>
        </div>

        <div className="bg-card border border-border rounded-xl p-4 mb-6 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            {sections.map((s) => {
              const Icon = s.icon;
              const hasErr = sectionHasError(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setActiveSection(s.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
                    activeSection === s.id
                      ? 'bg-primary text-white'
                      : hasErr
                      ? 'bg-destructive/10 text-destructive border border-destructive/30'
                      : 'bg-muted text-foreground hover:bg-muted/70'
                  }`}
                >
                  <Icon size={16} />
                  <span className="text-sm font-medium">{s.id + 1}. {s.title}</span>
                  {hasErr && <AlertCircle size={14} />}
                </button>
              );
            })}
          </div>
        </div>

        <form onSubmit={handleSubmit} noValidate className="bg-card border border-border rounded-xl p-6">
          {activeSection === 0 && (
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <User size={20} /> Section A: Applicant Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Applicant Name <span className="text-destructive">*</span></label>
                  <input data-testid="customer-name" type="text" name="customer_name" value={formData.customer_name} onChange={handleChange} className={inputCls('customer_name')} />
                  <ErrorMsg field="customer_name" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Mother's Name</label>
                  <input type="text" name="mother_name" value={formData.mother_name} onChange={handleChange} className={inputCls('mother_name')} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Date of Birth</label>
                  <input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} className={inputCls('date_of_birth')} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Marital Status</label>
                  <select name="marital_status" value={formData.marital_status} onChange={handleChange} className={inputCls('marital_status')}>
                    <option value="">Select...</option>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widowed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">PAN Number</label>
                  <input type="text" name="pan_number" value={formData.pan_number} onChange={handleChange} placeholder="ABCDE1234F" className={inputCls('pan_number')} />
                  <ErrorMsg field="pan_number" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Aadhaar Number</label>
                  <input type="text" name="aadhaar_number" value={formData.aadhaar_number} onChange={handleChange} placeholder="1234 5678 9012" className={inputCls('aadhaar_number')} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Mobile Number <span className="text-destructive">*</span></label>
                  <input data-testid="mobile-number" type="tel" name="mobile_number" value={formData.mobile_number} onChange={handleChange} placeholder="10-digit mobile" className={inputCls('mobile_number')} />
                  <ErrorMsg field="mobile_number" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email ID</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} className={inputCls('email')} />
                </div>
              </div>
            </div>
          )}

          {activeSection === 1 && (
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Building size={20} /> Section B: Business / Firm Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Firm Name</label>
                  <input type="text" name="firm_name" value={formData.firm_name} onChange={handleChange} className={inputCls('firm_name')} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Firm Registration Date</label>
                  <input type="date" name="firm_registration_date" value={formData.firm_registration_date} onChange={handleChange} className={inputCls('firm_registration_date')} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Firm Address</label>
                  <textarea rows="2" name="firm_address" value={formData.firm_address} onChange={handleChange} className={inputCls('firm_address')} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Firm Landmark</label>
                  <input type="text" name="firm_landmark" value={formData.firm_landmark} onChange={handleChange} className={inputCls('firm_landmark')} />
                </div>
              </div>
            </div>
          )}

          {activeSection === 2 && (
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Home size={20} /> Section C: Residential Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Current Residential Address</label>
                  <textarea rows="2" name="current_address" value={formData.current_address} onChange={handleChange} className={inputCls('current_address')} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Current Address Landmark</label>
                  <input type="text" name="current_address_landmark" value={formData.current_address_landmark} onChange={handleChange} className={inputCls('current_address_landmark')} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Permanent Address Landmark</label>
                  <input type="text" name="permanent_address_landmark" value={formData.permanent_address_landmark} onChange={handleChange} className={inputCls('permanent_address_landmark')} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Permanent Residential Address</label>
                  <textarea rows="2" name="permanent_address" value={formData.permanent_address} onChange={handleChange} className={inputCls('permanent_address')} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">City <span className="text-destructive">*</span></label>
                  <input data-testid="city" type="text" name="city" value={formData.city} onChange={handleChange} className={inputCls('city')} />
                  <ErrorMsg field="city" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">State <span className="text-destructive">*</span></label>
                  <input data-testid="state" type="text" name="state" value={formData.state} onChange={handleChange} className={inputCls('state')} />
                  <ErrorMsg field="state" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">PIN Code</label>
                  <input type="text" name="pin_code" value={formData.pin_code} onChange={handleChange} className={inputCls('pin_code')} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Alternate Mobile Number</label>
                  <input type="tel" name="alternate_number" value={formData.alternate_number} onChange={handleChange} className={inputCls('alternate_number')} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Alternate Email ID</label>
                  <input type="email" name="alternate_email" value={formData.alternate_email} onChange={handleChange} className={inputCls('alternate_email')} />
                </div>
              </div>
            </div>
          )}

          {activeSection === 3 && (
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <UserPlus size={20} /> Section D: Reference Details
              </h2>
              <div className="space-y-6">
                {['reference1', 'reference2'].map((refKey, idx) => (
                  <div key={refKey} className="p-4 rounded-lg border border-border bg-muted/30">
                    <h3 className="font-medium text-foreground mb-3">Reference {idx + 1}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Name</label>
                        <input type="text" value={formData[refKey].name} onChange={(e) => handleRefChange(refKey, 'name', e.target.value)} className={inputCls('ref')} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Mobile Number</label>
                        <input type="tel" value={formData[refKey].mobile} onChange={(e) => handleRefChange(refKey, 'mobile', e.target.value)} className={inputCls('ref')} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Relation</label>
                        <input type="text" value={formData[refKey].relation} onChange={(e) => handleRefChange(refKey, 'relation', e.target.value)} placeholder="e.g., Friend, Brother" className={inputCls('ref')} />
                      </div>
                    </div>
                  </div>
                ))}

                {/* Co-Applicant Toggle + Fields */}
                <div className="p-4 rounded-lg border border-border bg-muted/30" data-testid="co-applicant-block">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h3 className="font-medium text-foreground">Co-Applicant</h3>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">Has Co-Applicant?</span>
                      <button
                        type="button"
                        data-testid="has-co-applicant-toggle"
                        onClick={() => setFormData((prev) => ({
                          ...prev,
                          has_co_applicant: !prev.has_co_applicant,
                          ...(prev.has_co_applicant ? { co_applicant_name: '', co_applicant_mobile: '', co_applicant_email: '', co_applicant_relation: '' } : {}),
                        }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.has_co_applicant ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                        aria-checked={formData.has_co_applicant}
                        role="switch"
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.has_co_applicant ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                      <span className="text-sm font-medium text-foreground">{formData.has_co_applicant ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                  {formData.has_co_applicant && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Co-Applicant Name</label>
                        <input
                          data-testid="co-applicant-name-input"
                          type="text"
                          name="co_applicant_name"
                          value={formData.co_applicant_name}
                          onChange={handleChange}
                          className={inputCls('co_applicant_name')}
                          placeholder="Full name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Co-Applicant Mother's Name</label>
                        <input
                          data-testid="co-applicant-mother-name-input"
                          type="text"
                          name="co_applicant_mother_name"
                          value={formData.co_applicant_mother_name}
                          onChange={handleChange}
                          className={inputCls('co_applicant_mother_name')}
                          placeholder="Mother's full name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Mobile Number</label>
                        <input
                          data-testid="co-applicant-mobile-input"
                          type="tel"
                          name="co_applicant_mobile"
                          value={formData.co_applicant_mobile}
                          onChange={handleChange}
                          className={inputCls('co_applicant_mobile')}
                          placeholder="10-digit mobile"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Email ID</label>
                        <input
                          data-testid="co-applicant-email-input"
                          type="email"
                          name="co_applicant_email"
                          value={formData.co_applicant_email}
                          onChange={handleChange}
                          className={inputCls('co_applicant_email')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Relationship with Applicant</label>
                        <select
                          data-testid="co-applicant-relation-select"
                          name="co_applicant_relation"
                          value={formData.co_applicant_relation}
                          onChange={handleChange}
                          className={inputCls('co_applicant_relation')}
                        >
                          <option value="">Select…</option>
                          <option value="Spouse">Spouse</option>
                          <option value="Parent">Parent</option>
                          <option value="Child">Child</option>
                          <option value="Sibling">Sibling</option>
                          <option value="Business Partner">Business Partner</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div className="md:col-span-2 text-xs text-muted-foreground">
                        Co-applicant PAN &amp; Aadhaar documents can be uploaded from the Lead Details page once the lead is created.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeSection === 4 && (
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Wallet size={20} /> Section E: Loan Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Product Type (Loan Type) <span className="text-destructive">*</span></label>
                  <select data-testid="loan-type" name="loan_type" value={formData.loan_type} onChange={handleChange} className={inputCls('loan_type')}>
                    <option value="">Select Product...</option>
                    {products.length > 0 ? (
                      products.map((p) => (
                        <option key={p.id} value={p.name}>{p.name}</option>
                      ))
                    ) : (
                      <>
                        <option value="Personal Loan">Personal Loan</option>
                        <option value="Business Loan">Business Loan</option>
                        <option value="Home Loan">Home Loan</option>
                        <option value="Auto Loan">Auto Loan</option>
                        <option value="Education Loan">Education Loan</option>
                        <option value="Loan Against Property">Loan Against Property</option>
                        <option value="Credit Card">Credit Card</option>
                      </>
                    )}
                  </select>
                  <ErrorMsg field="loan_type" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Loan Amount <span className="text-destructive">*</span></label>
                  <input data-testid="loan-amount" type="number" name="loan_amount" value={formData.loan_amount} onChange={handleChange} placeholder="e.g., 500000" className={inputCls('loan_amount')} />
                  <ErrorMsg field="loan_amount" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Monthly Income</label>
                  <input type="number" name="monthly_income" value={formData.monthly_income} onChange={handleChange} className={inputCls('monthly_income')} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Business Vintage</label>
                  <input type="text" name="business_vintage" value={formData.business_vintage} onChange={handleChange} placeholder="e.g., 5 years" className={inputCls('business_vintage')} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Existing EMI</label>
                  <input type="number" name="existing_emi" value={formData.existing_emi} onChange={handleChange} className={inputCls('existing_emi')} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">CIBIL Score</label>
                  <input type="number" name="cibil_score" min="300" max="900" value={formData.cibil_score} onChange={handleChange} placeholder="300-900" className={inputCls('cibil_score')} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Lead Source <span className="text-destructive">*</span></label>
                  <select data-testid="lead-source" name="lead_source" value={formData.lead_source} onChange={handleChange} className={inputCls('lead_source')}>
                    <option value="">Select Source...</option>
                    {leadSources.length > 0 ? (
                      leadSources.map((s) => (
                        <option key={s.id} value={s.name}>{s.name}</option>
                      ))
                    ) : (
                      <>
                        <option value="Website">Website</option>
                        <option value="Reference">Reference</option>
                        <option value="Walk-in">Walk-in</option>
                        <option value="Social Media">Social Media</option>
                        <option value="Call Center">Call Center</option>
                        <option value="Campaign">Campaign</option>
                        <option value="DSA Partner">DSA Partner</option>
                        <option value="Other">Other</option>
                      </>
                    )}
                  </select>
                  <ErrorMsg field="lead_source" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Assigned Executive <span className="text-destructive">*</span></label>
                  <select data-testid="assigned-to" name="assigned_to" value={formData.assigned_to} onChange={handleChange} className={inputCls('assigned_to')}>
                    <option value="">-- Select Executive --</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.full_name} ({u.role.replace('_', ' ')})</option>
                    ))}
                  </select>
                  <ErrorMsg field="assigned_to" />
                  {users.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">No executives available. Please create users with role Sales Executive, Telecaller, Team Leader or Branch Manager.</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Initial Status</label>
                  <select data-testid="status" name="status" value={formData.status} onChange={handleChange} className={inputCls('status')}>
                    <option value="fresh">Fresh Lead</option>
                    <option value="contacted">Contacted</option>
                    <option value="interested">Interested</option>
                    <option value="documents_pending">Documents Pending</option>
                  </select>
                </div>
              </div>

              {/* Personal Loan Specific Fields — shown only when loan_type == 'Personal Loan' */}
              {formData.loan_type === 'Personal Loan' && (
                <div className="mt-6 pt-6 border-t border-border" data-testid="personal-loan-fields">
                  <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
                    <User size={18} /> Personal Loan — Employment Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium mb-2">Applicant Qualification</label>
                      <input data-testid="pl-qualification" type="text" name="qualification" value={formData.qualification} onChange={handleChange} placeholder="e.g., B.Tech, MBA" className={inputCls('qualification')} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Designation</label>
                      <input data-testid="pl-designation" type="text" name="designation" value={formData.designation} onChange={handleChange} placeholder="e.g., Manager" className={inputCls('designation')} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Current Company Name</label>
                      <input data-testid="pl-current-company" type="text" name="current_company_name" value={formData.current_company_name} onChange={handleChange} className={inputCls('current_company_name')} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Date of Joining (Current Company)</label>
                      <input data-testid="pl-date-of-joining" type="date" name="date_of_joining" value={formData.date_of_joining} onChange={handleChange} className={inputCls('date_of_joining')} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Current Company Work Experience</label>
                      <input data-testid="pl-current-exp" type="text" name="current_company_work_experience" value={formData.current_company_work_experience} onChange={handleChange} placeholder="e.g., 2 years 3 months" className={inputCls('current_company_work_experience')} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Total Work Experience</label>
                      <input data-testid="pl-total-exp" type="text" name="total_work_experience" value={formData.total_work_experience} onChange={handleChange} placeholder="e.g., 5 years" className={inputCls('total_work_experience')} />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-2">Office Address</label>
                      <textarea data-testid="pl-office-address" rows="2" name="office_address" value={formData.office_address} onChange={handleChange} className={inputCls('office_address')} />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-2">Office Landmark</label>
                      <input data-testid="pl-office-landmark" type="text" name="office_landmark" value={formData.office_landmark} onChange={handleChange} className={inputCls('office_landmark')} />
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">
                    Applicant Photo, Voter ID, Business/Residence/Office Photos can be uploaded from the <strong>Documents</strong> tab on the Lead Details page after creation.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <button
              type="button"
              onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
              disabled={activeSection === 0}
              className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted disabled:opacity-50"
            >
              <ChevronLeft size={18} /> Previous
            </button>
            <span className="text-sm text-muted-foreground">Section {activeSection + 1} of {sections.length}</span>
            {activeSection < sections.length - 1 ? (
              <button
                type="button"
                onClick={() => setActiveSection(activeSection + 1)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                Next <ChevronRight size={18} />
              </button>
            ) : (
              <button
                data-testid="submit-lead"
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg disabled:opacity-50"
              >
                {loading && <Loader2 className="animate-spin" size={20} />}
                {loading ? 'Creating...' : 'Submit Lead'}
              </button>
            )}
          </div>

          {Object.keys(errors).length > 0 && activeSection === 4 && (
            <div className="mt-4 p-4 rounded-lg bg-destructive/10 border border-destructive/30">
              <p className="text-sm font-medium text-destructive mb-2 flex items-center gap-2">
                <AlertCircle size={16} /> Please complete all mandatory fields:
              </p>
              <ul className="text-xs text-destructive list-disc list-inside">
                {Object.entries(errors).map(([field, msg]) => (
                  <li key={field}>{msg}</li>
                ))}
              </ul>
            </div>
          )}
        </form>
      </div>
    </DashboardLayout>
  );
};

export default LeadForm;
